/**
 * The Studio's calls on tricorder-session, through the gateway's /sessions/**
 * proxy (tricorder-session SPEC.md sections 2, 3, 7): the assistant's
 * conversation, and a protocol run's log.
 *
 * Creating a session on an agent RUNS it: POST /sessions takes the agent and
 * the first message and answers 202 with both. A session that pins no agent
 * (a protocol run, tricorder-session ADR-0009) is created alone, 201, and
 * written by typed appends.
 */
import { api, parseResponse, randomUUID } from '@ai4nr/tricorder-ui'

import {
  CreateAgentlessSessionResponseSchema,
  CreateSessionResponseSchema,
  MessageResponsePageSchema,
  MessageResponseSchema,
  SessionResponseSchema,
} from './schemas_session'

export const sessionsApi = {
  /** One session. Messages are read separately. */
  get: (id: string) =>
    api.get(`/sessions/${id}`).then((res) => parseResponse(SessionResponseSchema, res, `GET /sessions/${id}`)),

  /**
   * Every message after `after`, in seq order, following the list's
   * `Link: rel="next"` (tricorder-session SPEC.md section 2) to its last page.
   */
  messagesAfter: (id: string, after: number) =>
    api.getAllItems(`/sessions/${id}/messages?after=${after}`, { schema: MessageResponsePageSchema }),

  /**
   * Create a session on an agent and RUN it. `subject` cites what the session
   * is about (the Studio's assistant: `{type: 'protocol_version', id}`).
   */
  create: (agentSlug: string, firstMessage: string, options: { subject: { type: string; id: string }; idempotencyKey: string }) =>
    api
      .post(
        '/sessions',
        { agent: agentSlug, message: firstMessage, subject: options.subject },
        { headers: { 'Idempotency-Key': options.idempotencyKey } },
      )
      .then((res) => parseResponse(CreateSessionResponseSchema, res, 'POST /sessions')),

  /** Say something: write the user's message and queue the turn. */
  postMessage: (id: string, content: string, idempotencyKey: string = randomUUID()) =>
    api
      .post(`/sessions/${id}/messages`, { content }, { headers: { 'Idempotency-Key': idempotencyKey } })
      .then((res) => parseResponse(MessageResponseSchema, res, `POST /sessions/${id}/messages`)),

  /**
   * Create a session that pins no agent: a protocol run's log. `subject` cites
   * the party the run is about, `parent` the conversation it was offered in.
   * A retried key answers the first call's session.
   */
  createAgentless: (
    options: { subject?: { type: string; id: string }; parent?: string; title?: string },
    idempotencyKey: string,
  ) =>
    api
      .post(
        '/sessions',
        {
          ...(options.subject ? { subject: options.subject } : {}),
          ...(options.parent ? { parent: options.parent } : {}),
          ...(options.title ? { title: options.title } : {}),
        },
        { headers: { 'Idempotency-Key': idempotencyKey } },
      )
      .then((res) => parseResponse(CreateAgentlessSessionResponseSchema, res, 'POST /sessions (agentless)')),

  /**
   * Append a typed record (tricorder-session ADR-0008): `type` names the
   * payload's schema, and no turn starts. A retry with the same key answers
   * the first call's message, so a lost answer never appends twice.
   */
  appendTyped: (id: string, type: string, payload: unknown, idempotencyKey: string) =>
    api
      .post(`/sessions/${id}/messages`, { type, payload }, { headers: { 'Idempotency-Key': idempotencyKey } })
      .then((res) => parseResponse(MessageResponseSchema, res, `POST /sessions/${id}/messages (${type})`)),
}
